import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { exchangeImpersonationCode } from '@/api/impersonation';
import { ImpersonationEndedScreen } from '@/components/impersonation/ImpersonationEndedScreen';
import { LoadingState } from '@/components/ui';
import { beginImpersonationSession } from '@/lib/impersonation-session';

/** Where the impersonation tab lands after the exchange. */
const LANDING_PATH = '/designs';

type ExchangeOutcome = 'redirecting' | 'code_invalid';

/**
 * Exchanges per code, shared by every mount of this page in one page load.
 * StrictMode mounts, unmounts and mounts again; both effects get the same
 * promise, and the side effects (writing the session, reloading) live
 * inside it, so they run exactly once. The code is single-use on the API,
 * so a second request would only fail and could overwrite a success.
 */
const exchanges = new Map<string, Promise<ExchangeOutcome>>();

/**
 * The code of the exchange still in flight. The first mount removes the
 * fragment from the URL, so a remount reads no code any more and joins the
 * running exchange through this instead of reporting a missing code.
 */
let pendingCode: string | null = null;

function readCodeFromFragment(): string | null {
  const fragment = window.location.hash.replace(/^#/, '');
  if (!fragment) return null;
  const code = new URLSearchParams(fragment).get('code');
  return code ? code : null;
}

/** Drops the fragment right away: the code must not linger in history. */
function removeFragment(): void {
  if (!window.location.hash) return;
  const { pathname, search } = window.location;
  window.history.replaceState(window.history.state, '', `${pathname}${search}`);
}

function exchangeOnce(code: string): Promise<ExchangeOutcome> {
  const running = exchanges.get(code);
  if (running) return running;

  pendingCode = code;
  const exchange = exchangeImpersonationCode(code)
    .then((result): ExchangeOutcome => {
      beginImpersonationSession({
        accessToken: result.accessToken,
        expiresAt: result.expiresAt,
        user: result.user,
      });
      // A full reload, so every persisted store hydrates from the tab
      // storage the marker now selects (lib/impersonation-session.ts).
      window.location.replace(LANDING_PATH);
      return 'redirecting';
    })
    // A 400 (`impersonation_code_invalid`) and any other failure end the
    // same way: the code is spent or unusable, only a new jump helps. No
    // marker is written, so the tab stays an ordinary tab.
    .catch((): ExchangeOutcome => 'code_invalid')
    .finally(() => {
      if (pendingCode === code) pendingCode = null;
    });
  exchanges.set(code, exchange);
  return exchange;
}

/**
 * `/impersonate#code=…`: the landing page of the tab the admin opens from
 * the user list. Outside every guard: the tab has no session yet, and a
 * guard would send it to /login or use the admin's session.
 */
export function ImpersonatePage() {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState<ExchangeOutcome | 'pending'>(
    'pending'
  );

  useEffect(() => {
    const code = readCodeFromFragment() ?? pendingCode;
    removeFragment();

    if (!code) {
      setOutcome('code_invalid');
      return;
    }

    // No cancellation flag on purpose: under StrictMode the first mount's
    // result is the only one there is. A state update after a real unmount
    // is a no-op.
    void exchangeOnce(code).then(setOutcome);
  }, []);

  if (outcome === 'code_invalid') {
    return <ImpersonationEndedScreen reason="code_invalid" />;
  }

  return <LoadingState fullscreen label={t('common.loading')} />;
}
